<template>
  <div class="login-form-container">
    <form
      id="modules-login-form"
      data-abide
      novalidate
      class="login-form"
      @submit.prevent
      @mouseover="() => validate()"
    >
      <div class="welcome-panel">
        <a>{{ $t('loginForm.welcome') }}</a>
      </div>
      <template v-if="isAdMock">
        <div class="small-12 columns  info-input">
          <label>
            {{ $t('username') }}
            <div class="email-input-container">
              <input
                id="modules-login-form-input-username"
                v-model="username"
                type="text"
                name="username"
                class="email-input"
                :placeholder="$t('username')"
                required
                @input="validate"
              >
              <span class="email-sufix">{{ hkldSuffix }}</span>
            </div>
            <span class="form-error">{{ $t('loginForm.usernameTip') }}</span>
          </label>
        </div>

        <div class="small-12 columns info-input">
          <label for="modules-login-form-input-password">
            {{ $t('password') }}
          </label>
          <div class="password-input-container">
            <input
              id="modules-login-form-input-password"
              v-model="password"
              :type="isPasswordVisible ? 'text' : 'password'"
              name="password"
              class="text-input password-input"
              :placeholder="$t('password')"
              pattern=".{6,}"
              required
              @input="validate"
            >
            <button
              type="button"
              class="password-visibility-toggle"
              :aria-label="isPasswordVisible ? 'Hide password' : 'Show password'"
              :aria-pressed="isPasswordVisible.toString()"
              :title="isPasswordVisible ? 'Hide password' : 'Show password'"
              @click="togglePasswordVisibility"
            >
              <svg
                v-if="isPasswordVisible"
                class="password-visibility-icon"
                aria-hidden="true"
                width="40"
                height="38"
                viewBox="0 0 40 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 19.4179C10 19.4179 13.5496 13.4151 20 13.4151C26.4504 13.4151 30 19.4179 30 19.4179"
                  stroke="#0B3A59"
                  stroke-width="1.6"
                  stroke-linejoin="round"
                />
                <path
                  d="M23.5837 19.4179C23.5837 21.3899 21.9793 22.9884 20 22.9884"
                  stroke="#0B3A59"
                  stroke-width="1.6"
                  stroke-linejoin="round"
                />
                <path
                  d="M17.368 21.8435C16.776 21.2066 16.4141 20.3543 16.4141 19.4179C16.4141 17.4459 18.0185 15.8473 19.9977 15.8473C20.87 15.8473 21.6692 16.1578 22.2907 16.6739"
                  stroke="#0B3A59"
                  stroke-width="1.6"
                  stroke-linejoin="round"
                />
                <path
                  d="M27.5473 12.3351L13.2812 25.6649"
                  stroke="#0B3A59"
                  stroke-width="1.6"
                  stroke-linejoin="round"
                />
              </svg>
              <svg
                v-else
                class="password-visibility-icon"
                aria-hidden="true"
                width="40"
                height="38"
                viewBox="0 0 40 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 19.4768C10 19.4768 13.5496 13.4737 20 13.4737C26.4504 13.4737 30 19.4765 30 19.4765"
                  stroke="#0B3A59"
                  stroke-width="1.6"
                  stroke-linejoin="round"
                />
                <path
                  d="M22.5357 22.0015C23.9352 20.6071 23.9352 18.3464 22.5357 16.952C21.1362 15.5576 18.8672 15.5576 17.4676 16.952C16.0681 18.3464 16.0681 20.6071 17.4676 22.0015C18.8672 23.3959 21.1362 23.3959 22.5357 22.0015Z"
                  stroke="#0B3A59"
                  stroke-width="1.6"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </template>

      <div class="small-12 columns">
        <fieldset class="login-btn">
          <button
            id="modules-login-form-button-submit"
            class="btn btn--orange radius round"
            type="submit"
            :disabled="isAdMock && disabled"
            @click="() => submitForm()"
          >{{ $t('login') }}</button>
        </fieldset>
      </div>
    </form>
  </div>
</template>

<script>
import lodash from 'lodash'
import { onMounted, unref } from 'vue-demi'
import { mapGetters, mapMutations } from 'vuex'
import { resetCookies } from '@/api/auth/request'
import authentication from '@/authentication'
import loginMixin from '@/pages/loginMixin'
import { useStoreActions, useStoreGetters } from '@/store/index'
import types from '@/store/mutation-types'
import { resetStore } from '@/store/util'
import { EMAIL_HOST_NAME_SUFFIX } from '@/utils/consts'
import { isGlobalRegion, isOtherDomain } from './utils'

export default {
  name: 'InternalUserLoginForm',
  mixins: [loginMixin],
  setup () {
    const { allRegions, regionLanguageMap } = useStoreGetters('regions', ['allRegions', 'regionLanguageMap'])
    const { defaultSysLang } = useStoreGetters('language', ['defaultSysLang'])
    const { switchLang } = useStoreActions('language', ['switchLang'])
    const { initRegionData } = useStoreActions('regions', ['initRegionData'])
    function switchToDefaultLang () {
      const region = Object.values(unref(allRegions))
        .find(region =>
          !(isOtherDomain(region.domain) || isGlobalRegion(region)),
        )
      let language = unref(defaultSysLang)
      if (region) {
        language = lodash.get(unref(regionLanguageMap), [region.name, 0, 'name'], language)
      }
      switchLang(language)
    }
    onMounted(async () => {
      await initRegionData()
      switchToDefaultLang()
    })
    return {
      switchToDefaultLang,
    }
  },
  data () {
    return {
      abide: null,
      username: '',
      password: '',
      isPasswordVisible: false,
      disabled: true,
      hkldSuffix: EMAIL_HOST_NAME_SUFFIX,
    }
  },
  computed: {
    ...mapGetters('settings', ['isAdMock']),
  },
  watch: {
    password: 'onPasswordModelChangesVal',
  },
  created () {
    this.onPasswordModelChangesVal()
  },
  async mounted () {
    this.form = $('#modules-login-form')
    this.abide = new Foundation.Abide(this.form, {
      liveValidate: true,
    })
  },
  destroyed () {
    this.abide.destroy()
    this.abide = null
  },
  methods: {
    ...mapMutations('settings', {
      setJwt: types.JSON_WEB_TOKEN,
    }),
    onPasswordModelChangesVal () {
      if (!this.abide) {
        return false
      }
      this.abide.validateForm()
      this.disabled = $('input.is-invalid-input').length !== 0
      return true
    },
    validate () {
      this.username = (this.username || '').trim()
      this.abide.validateForm()
      this.disabled = $('input.is-invalid-input').length !== 0
      return !this.disabled
    },
    togglePasswordVisibility () {
      this.isPasswordVisible = !this.isPasswordVisible
    },
    async submitForm () {
      await resetCookies()
      resetStore()
      if (!this.isAdMock) {
        await authentication.signIn()
      } else {
        const username = this.username.trim().concat(this.hkldSuffix)
        await this.pwdLogin(username, this.password)
      }
    },
  },
}
</script>

<style lang='scss' scoped>
@import '~styles/login/loginForm';

.login-form-container {
  min-height: 350px;
}

.email-input-container{
  position:relative;
  border: solid 1px #e0e0e0;
}

.email-input{
  width: 63%;
  border: 0;
  margin: 0;
  color: #0C3A58;
}

.email-sufix{
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;

}

#modules-login-form-button-submit {
  margin-top: 10px;
  margin-bottom: 10px;
}
</style>
<style scoped lang='scss' src="styles/dashboard/style.scss"></style>
